import React, { createContext, useState, useCallback, useRef, useEffect } from 'react';
import { Snackbar, Alert, Button, Box } from '@mui/material';
import ReactDOM from 'react-dom';

export const CustomNotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'info', // 'success', 'error', 'info', 'warning'
    duration: 5000,
    actions: [] // Array of action buttons
  });
  
  // Reference to the main content box
  const mainContentRef = useRef(null);
  const [mainContentEl, setMainContentEl] = useState(null);
  
  // Find and update the main content element when component mounts
  useEffect(() => {
    const findMainContent = () => {
      const mainContentBox = document.querySelector('.main-content-box');
      if (mainContentBox) {
        setMainContentEl(mainContentBox);
        return mainContentBox;
      }
      return null;
    };
    
    // Try to find the element immediately
    let foundElement = findMainContent();
    
    // If not found, try again after a short delay to ensure DOM is fully loaded
    if (!foundElement) {
      const timeout = setTimeout(() => {
        findMainContent();
      }, 500);
      
      return () => clearTimeout(timeout);
    }
    
    // Cleanup function
    return () => {
      setMainContentEl(null);
    };
  }, []);
  
  const showNotification = useCallback((notificationData) => {
    // Close any existing notification first
    setNotification(prev => ({
      ...prev,
      open: false
    }));
    
    // Use setTimeout to ensure the previous notification is closed 
    // before showing the new one (prevents animation issues)
    setTimeout(() => {
      setNotification({
        open: true,
        message: notificationData.message,
        type: notificationData.type || 'info',
        duration: notificationData.autoHideDuration || notificationData.duration || 5000,
        actions: notificationData.actions || []
      });
      
      // Log notification for debugging
      console.log(`Showing notification: ${notificationData.message} (${notificationData.type || 'info'})`);
    }, 100);
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  }, []);

  const value = {
    showNotification,
    hideNotification
  };
  
  // Custom snackbar component to render inside the main content box
  const CustomSnackbar = () => {
    if (!notification.open) return null;
    
    return (
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.duration}
        onClose={hideNotification}
        sx={{
          position: 'fixed',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999
        }}
      >
        <Alert 
          onClose={hideNotification} 
          severity={notification.type}
          variant="filled"
          elevation={6}
          sx={{ 
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            fontSize: '1rem',
            padding: '12px 16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}
          action={
            notification.actions && notification.actions.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                {notification.actions.map((action, index) => (
                  <Button
                    key={index}
                    color="inherit"
                    size="small"
                    onClick={() => {
                      hideNotification();
                      if (action.onClick) action.onClick();
                    }}
                    sx={{ fontWeight: 'bold' }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            ) : null
          }
        >
          {notification.message}
        </Alert>
      </Snackbar>
    );
  };

  return (
    <CustomNotificationContext.Provider value={value}>
      {children}
      
      {/* Render the snackbar inside the main content box if it exists, otherwise in the default context */}
      {mainContentEl ? 
        ReactDOM.createPortal(<CustomSnackbar />, mainContentEl) :
        <CustomSnackbar />
      }
    </CustomNotificationContext.Provider>
  );
};

// Default export for use in the application
export default NotificationProvider;